import { useEffect, useRef, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { logoutUser } from '../redux/slices/authSlice';
import adminService from '../services/adminService';
import { useToast } from '../context/ToastContext';

/**
 * useAutoLogout - Global hook to monitor user inactivity.
 * Fetches settings from backend to determine if autoLogout is enabled
 * and what the sessionTimeout is. Logs the user out if inactive.
 */
export const useAutoLogout = () => {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const { isAuthenticated, user } = useSelector(state => state.auth);
    const { showToast } = useToast();
    
    const timeoutIdRef = useRef(null);
    const settingsRef = useRef({ autoLogout: false, sessionTimeout: 30 }); // Defaults

    // Fetch settings on mount or auth change
    useEffect(() => {
        if (!isAuthenticated) return;
        
        const fetchSettings = async () => {
            try {
                // Only root/admin can change this, but the setting applies to the platform.
                // Note: If non-admins cannot access /api/admin/settings, this request might fail.
                // In that case, we default to 30 mins if they are a standard user.
                const res = await adminService.getSettings();
                if (res?.data?.success && res.data.data?.settings) {
                    settingsRef.current = {
                        autoLogout: res.data.data.settings.autoLogout,
                        sessionTimeout: Number(res.data.data.settings.sessionTimeout) || 30
                    };
                }
            } catch (err) {
                // Silently fallback to defaults (e.g., if user is a driver without admin settings access)
                settingsRef.current = { autoLogout: true, sessionTimeout: 30 };
            }
        };

        fetchSettings();
    }, [isAuthenticated]);

    const performLogout = useCallback(() => {
        if (!isAuthenticated || !settingsRef.current.autoLogout) return;
        
        const role = user?.role;
        
        // Determine login path based on role
        let loginPath = '/login';
        if (role === 'admin') loginPath = '/admin/login';
        else if (role === 'dispatcher') loginPath = '/dispatcher/login';
        else if (role === 'driver') loginPath = '/driver/login';
        
        if (role) {
            localStorage.setItem('last_role', role);
        }
        
        dispatch(logoutUser());
        navigate(loginPath, { replace: true });
        
        // Show a helpful message
        setTimeout(() => {
            showToast('You have been automatically logged out due to inactivity.', 'info');
        }, 500);
    }, [dispatch, navigate, isAuthenticated, showToast, user]);

    const resetTimer = useCallback(() => {
        if (!isAuthenticated) return;
        
        // Clear existing timer
        if (timeoutIdRef.current) {
            clearTimeout(timeoutIdRef.current);
        }

        // Only set new timer if autoLogout is true
        // We wait a second to make sure settings have loaded, but fallback is fine
        const timeoutMs = (settingsRef.current.sessionTimeout || 30) * 60 * 1000;
        
        if (settingsRef.current.autoLogout) {
            timeoutIdRef.current = setTimeout(performLogout, timeoutMs);
        }
    }, [isAuthenticated, performLogout]);

    useEffect(() => {
        if (!isAuthenticated) {
            if (timeoutIdRef.current) clearTimeout(timeoutIdRef.current);
            return;
        }

        // Initialize timer
        resetTimer();

        // Listen for user activity
        const events = [
            'mousemove', 'mousedown', 'keydown', 
            'touchstart', 'scroll', 'wheel'
        ];

        // Use a throttled version of resetTimer to prevent excessive calls
        let throttleTimer;
        const throttledReset = () => {
            if (throttleTimer) return;
            throttleTimer = setTimeout(() => {
                resetTimer();
                throttleTimer = null;
            }, 1000); // Only reset timer max once per second
        };

        events.forEach(event => {
            window.addEventListener(event, throttledReset, { passive: true });
        });

        return () => {
            if (timeoutIdRef.current) clearTimeout(timeoutIdRef.current);
            if (throttleTimer) clearTimeout(throttleTimer);
            events.forEach(event => {
                window.removeEventListener(event, throttledReset);
            });
        };
    }, [isAuthenticated, resetTimer]);
};

export default useAutoLogout;
